import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import {
  Receipt, Plus, Pencil, Trash, ArrowsClockwise, MagnifyingGlass, X, FilePdf,
  CheckCircle, XCircle, Coins, FileXls, ArrowLeft, Copy, EnvelopeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getToken } from "@/lib/authToken";
const ESTADO_LABEL = {
  borrador: "Borrador",
  emitida: "Emitida",
  pagada: "Pagada",
  vencida: "Vencida",
  anulada: "Anulada",
};
const ESTADO_CLS = {
  borrador: "bg-slate-50 text-slate-700 border-slate-200",
  emitida: "bg-amber-50 text-amber-700 border-amber-200",
  pagada: "bg-emerald-50 text-emerald-700 border-emerald-200",
  vencida: "bg-rose-50 text-rose-700 border-rose-200",
  anulada: "bg-zinc-100 text-zinc-500 border-zinc-200 line-through",
};

const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(Number(n || 0));

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Facturas() {
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({ facturado_mes: 0, pendiente_cobro: 0, vencido: 0, vencido_count: 0, pendiente_cobro_count: 0, facturado_mes_count: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | factura | "new"
  const [filtros, setFiltros] = useState({ estado: "", serie: "", q: "", date_from: "", date_to: "" });
  const [exportOpen, setExportOpen] = useState(false);
  const [emailing, setEmailing] = useState(null); // factura | null
  const [clientes, setClientes] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.estado) params.set("estado", filtros.estado);
      if (filtros.serie) params.set("serie", filtros.serie);
      if (filtros.q) params.set("q", filtros.q);
      if (filtros.date_from) params.set("date_from", filtros.date_from);
      if (filtros.date_to) params.set("date_to", filtros.date_to);
      const [r, k, c] = await Promise.all([
        api.get(`/facturacion/facturas${params.toString() ? `?${params}` : ""}`),
        api.get("/facturacion/facturas/kpis"),
        api.get("/facturacion/clientes?active=true"),
      ]);
      setItems(r.data || []);
      setKpis(k.data || {});
      setClientes(c.data || []);
    } catch (e) {
      console.error(e); toast.error("No se pudieron cargar las facturas");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadPdf = async (f) => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/facturacion/facturas/${f.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Error PDF");
      const blob = await res.blob();
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `factura_${(f.numero_completo || f.id.slice(0, 8)).replace(/\//g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error generando PDF"); }
  };

  const onAction = async (f, action) => {
    const map = {
      emit: { url: `/facturacion/facturas/${f.id}/emit`, ok: "Factura emitida", confirm: "¿Emitir factura? Se asignará número correlativo y no podrá modificarse." },
      pay: { url: `/facturacion/facturas/${f.id}/pay`, ok: "Factura marcada pagada", confirm: "¿Marcar como pagada?" },
      cancel: { url: `/facturacion/facturas/${f.id}/cancel`, ok: "Factura anulada", confirm: "¿Anular factura?" },
    };
    const cfg = map[action];
    if (cfg.confirm && !window.confirm(cfg.confirm)) return;
    try {
      await api.post(cfg.url, {});
      toast.success(cfg.ok);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const onDelete = async (f) => {
    if (!window.confirm(`¿Eliminar borrador?`)) return;
    try {
      await api.delete(`/facturacion/facturas/${f.id}`);
      toast.success("Borrador eliminado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const filtered = useMemo(() => items, [items]);

  if (editing) {
    return (
      <FacturaEditor
        initial={editing === "new" ? null : editing}
        clientes={clientes}
        onClose={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight inline-flex items-center gap-2">
            <Receipt className="w-6 h-6 text-brand" weight="duotone" />
            Facturas del Gabinete
          </h1>
          <p className="text-xs text-muted mt-1">Emite, gestiona el cobro y exporta para gestoría.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="fact-export" onClick={() => setExportOpen(true)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-border hover:border-border-strong bg-white">
            <FileXls className="w-3.5 h-3.5" weight="bold" /> Exportar gestoría
          </button>
          <button data-testid="fact-new" onClick={() => setEditing("new")} className="btn-brand inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md font-semibold">
            <Plus className="w-3.5 h-3.5" weight="bold" /> Nueva factura
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI label="Facturado este mes" value={fmtEUR(kpis.facturado_mes)} sub={`${kpis.facturado_mes_count} facturas`} tone="emerald" />
        <KPI label="Pendiente de cobro" value={fmtEUR(kpis.pendiente_cobro)} sub={`${kpis.pendiente_cobro_count} facturas`} tone="amber" />
        <KPI label="Vencido" value={fmtEUR(kpis.vencido)} sub={`${kpis.vencido_count} facturas`} tone="rose" />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 card-elevated p-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" weight="bold" />
          <input value={filtros.q} onChange={(e) => setFiltros({...filtros, q: e.target.value})} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar por nº o cliente..." className="!pl-9 !text-xs" />
        </div>
        <select value={filtros.estado} onChange={(e) => { setFiltros({...filtros, estado: e.target.value}); setTimeout(load, 0); }} className="!text-xs">
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filtros.serie} onChange={(e) => { setFiltros({...filtros, serie: e.target.value}); setTimeout(load, 0); }} className="!text-xs">
          <option value="">Todas las series</option>
          <option value="A">A · Aseguradoras</option>
          <option value="P">P · Particulares</option>
        </select>
        <input type="date" value={filtros.date_from} onChange={(e) => setFiltros({...filtros, date_from: e.target.value})} className="!text-xs" />
        <span className="text-muted">→</span>
        <input type="date" value={filtros.date_to} onChange={(e) => setFiltros({...filtros, date_to: e.target.value})} className="!text-xs" />
        <button onClick={load} className="text-xs px-3 py-2 rounded-md border border-border hover:border-border-strong bg-white inline-flex items-center gap-1.5">
          <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" /> Aplicar
        </button>
      </div>

      {/* Tabla */}
      <div className="card-elevated overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted">Sin facturas. Crea la primera.</div>
        ) : (
          <table className="w-full text-sm" data-testid="fact-table">
            <thead className="thead-modern">
              <tr>
                <th>Nº</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th className="!text-right">Total</th>
                <th>Estado</th>
                <th>Vencimiento</th>
                <th className="!text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f, idx) => {
                const overdue = f.estado === "emitida" && f.fecha_vencimiento && f.fecha_vencimiento < todayStr();
                return (
                  <tr key={f.id} className={`border-b border-[#F1F3F5] hover:bg-[#F5F6F8] transition ${idx % 2 ? "bg-[#FBFBFC]" : ""}`}>
                    <td className="px-3 py-2.5 text-[12.5px] tabular-nums">{f.numero_completo || <span className="italic text-muted">borrador</span>}</td>
                    <td className="px-3 py-2.5 text-xs">{f.fecha_emision || "—"}</td>
                    <td className="px-3 py-2.5">{f.cliente_snapshot?.nombre_fiscal || clientes.find((c) => c.id === f.cliente_id)?.nombre_fiscal || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{fmtEUR(f.total_factura)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${ESTADO_CLS[f.estado]}`}>{ESTADO_LABEL[f.estado]}</span>
                      {overdue && <span className="ml-1 text-[10px] text-rose-600 font-bold">VENCIDA</span>}
                    </td>
                    <td className="px-3 py-2.5 text-xs">{f.fecha_vencimiento || "—"}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => downloadPdf(f)} className="text-muted hover:text-brand p-1.5" title="PDF" data-testid={`fact-pdf-${f.id}`}><FilePdf className="w-3.5 h-3.5" weight="bold" /></button>
                      <button onClick={() => setEditing(f)} className="text-muted hover:text-brand p-1.5" title="Editar/Ver" data-testid={`fact-edit-${f.id}`}><Pencil className="w-3.5 h-3.5" weight="bold" /></button>
                      {f.estado === "borrador" && (
                        <>
                          <button onClick={() => onAction(f, "emit")} className="text-amber-700 hover:text-amber-900 p-1.5" title="Emitir" data-testid={`fact-emit-${f.id}`}><CheckCircle className="w-3.5 h-3.5" weight="bold" /></button>
                          <button onClick={() => onDelete(f)} className="text-muted hover:text-rose-600 p-1.5" title="Eliminar"><Trash className="w-3.5 h-3.5" weight="bold" /></button>
                        </>
                      )}
                      {f.estado === "emitida" && (
                        <>
                          <button onClick={() => setEmailing(f)} className="text-amber-700 hover:text-amber-900 p-1.5" title="Enviar por email" data-testid={`fact-email-${f.id}`}><EnvelopeSimple className="w-3.5 h-3.5" weight="bold" /></button>
                          <button onClick={() => onAction(f, "pay")} className="text-emerald-700 hover:text-emerald-900 p-1.5" title="Marcar pagada" data-testid={`fact-pay-${f.id}`}><Coins className="w-3.5 h-3.5" weight="bold" /></button>
                          <button onClick={() => onAction(f, "cancel")} className="text-muted hover:text-rose-600 p-1.5" title="Anular"><XCircle className="w-3.5 h-3.5" weight="bold" /></button>
                        </>
                      )}
                      {f.estado === "pagada" && (
                        <button onClick={() => setEmailing(f)} className="text-amber-700 hover:text-amber-900 p-1.5" title="Reenviar por email" data-testid={`fact-email-${f.id}`}><EnvelopeSimple className="w-3.5 h-3.5" weight="bold" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {exportOpen && <ExportModal onClose={() => setExportOpen(false)} />}
      {emailing && <SendEmailModal factura={emailing} onClose={() => setEmailing(null)} onSent={() => { setEmailing(null); load(); }} />}
    </div>
  );
}

function KPI({ label, value, sub, tone }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/50",
    amber: "border-amber-200 bg-amber-50/50",
    rose: "border-rose-200 bg-rose-50/50",
  };
  return (
    <div className={`card-elevated p-4 border ${tones[tone] || "border-border"}`}>
      <div className="text-[11px] font-semibold text-muted uppercase tracking-wide">{label}</div>
      <div className="font-display font-bold text-2xl mt-1">{value}</div>
      <div className="text-xs text-muted">{sub}</div>
    </div>
  );
}

// ---------------- Editor ----------------

function emptyLinea() {
  return { id: crypto.randomUUID(), concepto: "", cantidad: 1, precio_unitario: 0, descuento_pct: 0, iva_pct: 21 };
}

function FacturaEditor({ initial, clientes, onClose }) {
  const [data, setData] = useState(() => initial || {
    cliente_id: clientes[0]?.id || "",
    serie: "A",
    fecha_emision: todayStr(),
    lineas: [emptyLinea()],
    retencion_irpf_pct: 0,
    notas: "",
    estado: "borrador",
  });
  const [saving, setSaving] = useState(false);
  const isReadOnly = data.id && data.estado !== "borrador";

  const setF = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const setLinea = (i, k, v) => setData((p) => ({ ...p, lineas: p.lineas.map((l, j) => j === i ? { ...l, [k]: v } : l) }));
  const addLinea = () => setData((p) => ({ ...p, lineas: [...p.lineas, emptyLinea()] }));
  const delLinea = (i) => setData((p) => ({ ...p, lineas: p.lineas.filter((_, j) => j !== i) }));

  // Auto-actualizar IRPF al cambiar cliente
  useEffect(() => {
    if (!data.cliente_id || data.id) return;
    const cli = clientes.find((c) => c.id === data.cliente_id);
    if (cli && (data.retencion_irpf_pct === 0 || data.retencion_irpf_pct == null)) {
      setF("retencion_irpf_pct", cli.irpf_default_pct || 0);
    }
    // eslint-disable-next-line
  }, [data.cliente_id]);

  const totales = useMemo(() => {
    let base = 0, iva = 0;
    for (const l of data.lineas || []) {
      const sub = Number(l.cantidad || 0) * Number(l.precio_unitario || 0) * (1 - Number(l.descuento_pct || 0) / 100);
      base += sub;
      iva += sub * Number(l.iva_pct || 0) / 100;
    }
    const irpf = base * Number(data.retencion_irpf_pct || 0) / 100;
    return { base, iva, irpf, total: base + iva - irpf };
  }, [data.lineas, data.retencion_irpf_pct]);

  const save = async () => {
    if (!data.cliente_id) { toast.error("Selecciona un cliente"); return; }
    if (!data.lineas?.length) { toast.error("Añade al menos una línea"); return; }
    setSaving(true);
    try {
      const payload = {
        cliente_id: data.cliente_id,
        serie: data.serie,
        fecha_emision: data.fecha_emision,
        lineas: data.lineas.map(({ id, ...l }) => ({
          ...l,
          cantidad: Number(l.cantidad) || 0,
          precio_unitario: Number(l.precio_unitario) || 0,
          descuento_pct: Number(l.descuento_pct) || 0,
          iva_pct: Number(l.iva_pct) || 0,
        })),
        retencion_irpf_pct: Number(data.retencion_irpf_pct) || 0,
        notas: data.notas,
      };
      if (data.id) {
        await api.put(`/facturacion/facturas/${data.id}`, payload);
        toast.success("Factura actualizada");
      } else {
        await api.post("/facturacion/facturas", payload);
        toast.success("Borrador creado");
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const cli = clientes.find((c) => c.id === data.cliente_id);

  return (
    <div className="p-6 space-y-4 animate-fade-in max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <button onClick={onClose} className="text-muted hover:text-foreground inline-flex items-center gap-1 text-xs">
          <ArrowLeft className="w-4 h-4" weight="bold" /> Volver
        </button>
        <div className="flex-1">
          <h1 className="font-display font-bold text-xl tracking-tight inline-flex items-center gap-2">
            <Receipt className="w-5 h-5 text-brand" weight="duotone" />
            {data.id ? `Factura ${data.numero_completo || "(borrador)"}` : "Nueva factura"}
          </h1>
          {isReadOnly && <p className="text-[11px] text-amber-700 font-semibold mt-0.5">Esta factura está {data.estado.toUpperCase()}: solo lectura.</p>}
        </div>
        {!isReadOnly && (
          <button onClick={save} disabled={saving} data-testid="fact-form-save" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold">
            {saving ? "Guardando..." : "Guardar borrador"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Cabecera */}
          <div className="card-elevated p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Cliente *">
                <select value={data.cliente_id} onChange={(e) => setF("cliente_id", e.target.value)} disabled={isReadOnly} className="!text-xs" data-testid="fact-form-cliente">
                  <option value="">— elige cliente —</option>
                  {clientes.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre_fiscal}</option>)}
                </select>
              </Field>
              <Field label="Serie">
                <select value={data.serie} onChange={(e) => setF("serie", e.target.value)} disabled={isReadOnly || !!data.numero_completo} className="!text-xs">
                  <option value="A">A · Aseguradoras</option>
                  <option value="P">P · Particulares</option>
                </select>
              </Field>
              <Field label="Fecha emisión">
                <input type="date" value={data.fecha_emision || ""} onChange={(e) => setF("fecha_emision", e.target.value)} disabled={isReadOnly} className="!text-xs" />
              </Field>
            </div>
            {cli && (
              <div className="text-[11px] text-muted bg-surface rounded p-2">
                <strong>{cli.nombre_fiscal}</strong> · {cli.cif_nif || "sin CIF"} · {cli.condiciones_pago_dias} días
                {cli.direccion && <> · {cli.direccion}</>}
              </div>
            )}
          </div>

          {/* Líneas */}
          <div className="card-elevated overflow-hidden">
            <div className="px-4 py-2 bg-foreground text-white text-xs font-bold flex items-center justify-between">
              <span>Líneas</span>
              {!isReadOnly && <button onClick={addLinea} className="text-[10px] inline-flex items-center gap-1 hover:underline" data-testid="fact-add-linea"><Plus className="w-3 h-3" weight="bold" /> Añadir</button>}
            </div>
            <table className="w-full text-xs">
              <thead className="bg-surface">
                <tr>
                  <th className="text-left px-2 py-1.5">Concepto</th>
                  <th className="px-1 py-1.5 w-16 text-right">Cant.</th>
                  <th className="px-1 py-1.5 w-24 text-right">Precio</th>
                  <th className="px-1 py-1.5 w-16 text-right">Dto%</th>
                  <th className="px-1 py-1.5 w-16 text-right">IVA%</th>
                  <th className="px-1 py-1.5 w-24 text-right">Subtotal</th>
                  {!isReadOnly && <th className="w-8"></th>}
                </tr>
              </thead>
              <tbody>
                {data.lineas.map((l, i) => {
                  const sub = Number(l.cantidad || 0) * Number(l.precio_unitario || 0) * (1 - Number(l.descuento_pct || 0) / 100);
                  return (
                    <tr key={l.id} className="border-t border-border">
                      <td className="px-2 py-1"><input value={l.concepto} onChange={(e) => setLinea(i, "concepto", e.target.value)} disabled={isReadOnly} className="!text-xs !py-1" data-testid={`fact-linea-concepto-${i}`} /></td>
                      <td className="px-1 py-1"><input type="number" step="0.01" value={l.cantidad} onChange={(e) => setLinea(i, "cantidad", e.target.value)} disabled={isReadOnly} className="!text-xs !py-1 text-right" /></td>
                      <td className="px-1 py-1"><input type="number" step="0.01" value={l.precio_unitario} onChange={(e) => setLinea(i, "precio_unitario", e.target.value)} disabled={isReadOnly} className="!text-xs !py-1 text-right" /></td>
                      <td className="px-1 py-1"><input type="number" step="0.01" value={l.descuento_pct} onChange={(e) => setLinea(i, "descuento_pct", e.target.value)} disabled={isReadOnly} className="!text-xs !py-1 text-right" /></td>
                      <td className="px-1 py-1"><input type="number" step="0.01" value={l.iva_pct} onChange={(e) => setLinea(i, "iva_pct", e.target.value)} disabled={isReadOnly} className="!text-xs !py-1 text-right" /></td>
                      <td className="px-1 py-1 text-right tabular-nums">{fmtEUR(sub)}</td>
                      {!isReadOnly && (
                        <td className="px-1 py-1 text-center">
                          <button onClick={() => delLinea(i)} className="text-muted hover:text-rose-600"><Trash className="w-3 h-3" weight="bold" /></button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="card-elevated p-4">
            <Field label="Notas (aparecerán en el PDF)">
              <textarea value={data.notas || ""} onChange={(e) => setF("notas", e.target.value)} rows={3} disabled={isReadOnly} className="!text-xs" />
            </Field>
          </div>
        </div>

        {/* Totales lateral */}
        <div className="space-y-4">
          <div className="card-elevated p-4 space-y-2">
            <h3 className="font-display font-bold text-sm">Resumen</h3>
            <div className="flex justify-between text-xs"><span className="text-muted">Base imponible</span><span className="tabular-nums">{fmtEUR(totales.base)}</span></div>
            <div className="flex justify-between text-xs"><span className="text-muted">IVA</span><span className="tabular-nums">{fmtEUR(totales.iva)}</span></div>
            <Field label="Retención IRPF (%)">
              <input type="number" step="0.01" value={data.retencion_irpf_pct} onChange={(e) => setF("retencion_irpf_pct", e.target.value)} disabled={isReadOnly} className="!text-xs" />
            </Field>
            {totales.irpf > 0 && (
              <div className="flex justify-between text-xs text-rose-700"><span>Retención</span><span className="tabular-nums">- {fmtEUR(totales.irpf)}</span></div>
            )}
            <div className="border-t border-border pt-2 flex justify-between text-sm font-bold">
              <span>TOTAL</span><span className="tabular-nums">{fmtEUR(totales.total)}</span>
            </div>
          </div>

          {data.id && (
            <div className="card-elevated p-4 space-y-2 text-xs">
              <h3 className="font-display font-bold text-sm">Estado</h3>
              <div className="text-muted">Estado actual: <strong>{ESTADO_LABEL[data.estado]}</strong></div>
              {data.numero_completo && <div className="text-muted">Nº: <strong className="tabular-nums">{data.numero_completo}</strong></div>}
              {data.fecha_vencimiento && <div className="text-muted">Vence: <strong>{data.fecha_vencimiento}</strong></div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted block mb-1">{label}</span>
      {children}
    </label>
  );
}

function ExportModal({ onClose }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(`${API}/facturacion/facturas/export/xlsx?date_from=${from}&date_to=${to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Error");
      const blob = await res.blob();
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url; a.download = `facturas_${from}_${to}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) { toast.error("Error generando Excel"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-5 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Exportar para gestoría</h2>
          <button onClick={onClose}><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <p className="text-xs text-muted">Genera un Excel con todas las facturas <strong>emitidas o pagadas</strong> en el rango.</p>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Desde"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="!text-xs" /></Field>
            <Field label="Hasta"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="!text-xs" /></Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-border bg-surface/30">
          <button onClick={onClose} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button onClick={download} disabled={busy} data-testid="fact-export-download" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold">
            {busy ? "Generando..." : "Descargar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SendEmailModal({ factura, onClose, onSent }) {
  const cliEmail = factura?.cliente?.email || "";
  const numero = factura?.numero_completo || factura?.id?.slice(0, 8);
  const [to, setTo] = useState(cliEmail);
  const [cc, setCc] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!to.trim()) { toast.error("Indica al menos un destinatario"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/facturacion/facturas/${factura.id}/send-email`, {
        to, cc: cc || undefined, message: message || undefined,
      });
      if (r.data?.ok) {
        toast.success(`Factura enviada a ${to}`);
        onSent();
      } else if (r.data?.reason === "smtp-not-configured") {
        toast.error("SMTP no configurado. Pídele al administrador que rellene SMTP_PASSWORD en backend/.env.");
      } else {
        toast.error(r.data?.error || "No se pudo enviar el email");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error de red");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="fact-email-modal">
        <div className="px-6 pt-5 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Enviar factura {numero}</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <p className="text-xs text-muted">Se enviará el PDF como adjunto al destinatario indicado.</p>
          <Field label="Para *">
            <input type="email" value={to} onChange={(e) => setTo(e.target.value)} required data-testid="fact-email-to" className="!text-xs" placeholder="cliente@empresa.com" />
          </Field>
          <Field label="Cc (opcional)">
            <input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="fact-email-cc" className="!text-xs" placeholder="copia@empresa.com, otro@x.com" />
          </Field>
          <Field label="Mensaje adicional (opcional)">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="fact-email-message" className="!text-xs" placeholder="Cualquier comentario que quieras añadir al email." />
          </Field>
        </div>
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-border bg-surface/30">
          <button type="button" onClick={onClose} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button type="submit" disabled={busy} data-testid="fact-email-send" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <EnvelopeSimple className="w-3 h-3" weight="bold" />
            {busy ? "Enviando..." : "Enviar"}
          </button>
        </div>
      </form>
    </div>
  );
}

